import { z } from 'zod'

// ============================================================================
// Error Messages
// ============================================================================

export * from './constants'
export * from './error-messages'
export * from './state-machines'
export * from './url-id'
// ============================================================================
// Primitives
// ============================================================================

export const EmailSchema = z.string().email()
export type Email = z.infer<typeof EmailSchema>

/**
 * A string that must parse to a valid date/timestamp.
 *
 * Date/timestamp values that reach SQL casts (`::date` / `::timestamptz`) are
 * serialized by postgres.js via `new Date(x).toISOString()`. An unparseable
 * string throws RangeError("Invalid Date") at the driver and surfaces as a 500.
 * Validate here so the API rejects it with a 422 instead.
 */
export const ZodDateString = z
  .string()
  .refine((v) => !Number.isNaN(Date.parse(v)), { message: 'invalid_date' })

// ============================================================================
// API
// ============================================================================

export const ApiResponseSchema = <T extends z.ZodType>(dataSchema: T) =>
  z.object({
    success: z.boolean(),
    data: dataSchema.optional(),
    error: z.string().optional(),
  })

export type ApiResponse<T> = {
  success: boolean
  data?: T
  error?: string
}

export const ZodPaginationQuery = z.object({
  page: z.coerce.number().min(1).default(1),
  limit: z.coerce.number().min(1).max(100).default(10),
})

export type PaginationQuery = z.infer<typeof ZodPaginationQuery>

export const ZodPaginatedResponse = <T extends z.ZodTypeAny>(dataSchema: T) =>
  z.object({
    data: z.array(dataSchema),
    total: z.number(),
    page: z.number(),
    limit: z.number(),
    total_pages: z.number(),
  })

export type PaginatedResponse<T extends z.ZodTypeAny> = z.infer<
  ReturnType<typeof ZodPaginatedResponse<T>>
>

export const ZodSuccessResponse = z.object({
  success: z.boolean(),
})

export type SuccessResponse = z.infer<typeof ZodSuccessResponse>

export const ZodErrorResponse = z.object({
  error: z.string(),
  code: z.string().optional(),
  trace: z.string().optional(),
})

export type ErrorResponse = z.infer<typeof ZodErrorResponse>
